<template>
  <div class="app-admin-page">
    <div class="app-admin-card">
      <div class="toolbar">
        <div>
          <div class="title">Bilibili 登录</div>
          <div class="muted">后台接口会优先使用数据库中保存的 Cookie，失效时可在此扫码更新。</div>
        </div>
        <div class="toolbar-actions">
          <el-button icon="el-icon-refresh" @click="loadStatus(true)">校验状态</el-button>
          <el-button type="primary" icon="el-icon-full-screen" @click="startLogin">生成二维码</el-button>
        </div>
      </div>

      <el-row :gutter="20">
        <el-col :span="15">
          <div class="section">
            <div class="section-title">当前登录态</div>
            <div class="status-line">
              <el-tag :type="status.valid ? 'success' : 'danger'" size="medium">
                {{ status.valid ? "Cookie 有效" : "Cookie 无效" }}
              </el-tag>
              <span class="status-text">{{ status.valid ? "现有 Cookie 可继续使用" : (status.reason || "请扫码登录") }}</span>
            </div>

            <div v-if="status.valid && status.user" class="user-panel">
              <img class="avatar" :src="status.user.avatar" referrerpolicy="no-referrer" />
              <div class="user-main">
                <div class="user-name">{{ status.user.name || "Bilibili 用户" }}</div>
                <div class="muted">UID：{{ status.user.uid || "-" }} · Lv.{{ status.user.level || 0 }}</div>
              </div>
            </div>

            <el-descriptions :column="2" border class="meta">
              <el-descriptions-item label="来源">{{ cookie.source || "-" }}</el-descriptions-item>
              <el-descriptions-item label="刷新令牌">{{ cookie.has_refresh_token ? "已保存" : "缺失" }}</el-descriptions-item>
              <el-descriptions-item label="过期时间">{{ formatTime(cookie.expires_at) }}</el-descriptions-item>
              <el-descriptions-item label="上次校验">{{ formatTime(cookie.last_check_at) }}</el-descriptions-item>
              <el-descriptions-item label="上次刷新">{{ formatTime(cookie.last_refresh_at) }}</el-descriptions-item>
              <el-descriptions-item label="刷新检查">{{ formatTime(cookie.last_refresh_check_at) }}</el-descriptions-item>
            </el-descriptions>

            <el-alert
              v-if="cookie.refresh_error"
              class="alert"
              type="warning"
              :closable="false"
              show-icon
              :title="cookie.refresh_error"
            />

            <div class="button-row">
              <el-button type="success" icon="el-icon-circle-check" @click="forceRefresh">立即刷新 Cookie</el-button>
              <el-button type="danger" icon="el-icon-delete" @click="logout">清除 Cookie</el-button>
            </div>
          </div>
        </el-col>

        <el-col :span="9">
          <div class="section qr-section">
            <div class="section-title">扫码登录</div>
            <div class="qr-box" :class="{ empty: !login.qrcode }">
              <img v-if="login.qrcode" :src="login.qrcode" class="qr-image" referrerpolicy="no-referrer" />
              <i v-else class="el-icon-full-screen qr-placeholder"></i>
            </div>
            <div class="login-message">{{ login.message || "等待生成二维码" }}</div>
            <div v-if="login.error" class="login-error">{{ login.error }}</div>
            <div class="button-row compact">
              <el-button size="small" type="primary" @click="startLogin">重新生成</el-button>
              <el-button size="small" @click="cancelLogin">取消</el-button>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import { post } from "../axios/request.js";
import { formatTime, isSuccess } from "./appAdminUtils.js";

export default {
  name: "appBilibili",
  data() {
    return {
      status: {
        configured: false,
        valid: false,
        reason: "",
        user: null,
      },
      cookie: {},
      login: {},
      timer: null,
      loading: false,
    };
  },
  mounted() {
    this.loadStatus(true);
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    formatTime,
    applyPayload(payload) {
      const data = payload || {};
      this.status = data;
      this.cookie = data.cookie || {};
      this.login = data.login || {};
      if (this.login.active) {
        this.startPolling();
      } else {
        this.stopPolling();
      }
    },
    loadStatus(force) {
      if (this.loading) {
        return;
      }
      this.loading = true;
      post("/api/admin/bilibili/status", { force_check: force ? 1 : 0 }).then((res) => {
        if (isSuccess(res)) {
          this.applyPayload(res.data.data || {});
        } else {
          this.$message.error(res.data.msg || "Bilibili 状态获取失败");
        }
      }).finally(() => {
        this.loading = false;
      });
    },
    startLogin() {
      post("/api/admin/bilibili/login/start", {}).then((res) => {
        if (isSuccess(res)) {
          const data = res.data.data || {};
          this.login = data.login || {};
          if (data.status) {
            this.status = data.status;
            this.cookie = data.status.cookie || {};
          }
          this.startPolling();
        } else {
          this.$message.error(res.data.msg || "二维码生成失败");
        }
      });
    },
    checkLogin() {
      post("/api/admin/bilibili/login/check", {}).then((res) => {
        if (!isSuccess(res)) {
          this.$message.error(res.data.msg || "扫码状态检查失败");
          this.stopPolling();
          return;
        }
        const data = res.data.data || {};
        this.login = data.login || {};
        if (data.status) {
          this.status = data.status;
          this.cookie = data.status.cookie || {};
        }
        if (this.login.state === "done") {
          if (this.status.valid) {
            this.$notify({ title: "登录成功", message: "Bilibili Cookie 已替换为最新扫码 Cookie", type: "success" });
          } else {
            this.$message.error(this.login.error || this.status.reason || "新 Cookie 校验失败");
          }
          this.stopPolling();
        } else if (!this.login.active) {
          this.stopPolling();
        }
      });
    },
    startPolling() {
      if (this.timer) {
        return;
      }
      this.timer = setInterval(this.checkLogin, 2500);
    },
    stopPolling() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    cancelLogin() {
      post("/api/admin/bilibili/login/cancel", {}).then((res) => {
        if (isSuccess(res)) {
          const data = res.data.data || {};
          this.login = data.login || {};
          if (data.status) {
            this.status = data.status;
            this.cookie = data.status.cookie || {};
          }
          this.stopPolling();
        }
      });
    },
    forceRefresh() {
      post("/api/admin/bilibili/refresh", {}).then((res) => {
        if (isSuccess(res)) {
          this.applyPayload(res.data.data || {});
          this.$notify({ title: "刷新完成", message: res.data.msg, type: "success" });
        } else {
          this.$message.error(res.data.msg || "Cookie 刷新失败");
          if (res.data.data) {
            this.applyPayload(res.data.data);
          }
        }
      });
    },
    logout() {
      this.$confirm("清除后会立即生成新的扫码二维码，确定继续吗？", "清除 Bilibili Cookie", { type: "warning" }).then(() => {
        post("/api/admin/bilibili/logout", {}).then((res) => {
          if (isSuccess(res)) {
            this.applyPayload(res.data.data || {});
            this.$notify({ title: "已清除", message: res.data.msg, type: "success" });
          } else {
            this.$message.error(res.data.msg || "清除失败");
          }
        });
      });
    },
  },
};
</script>

<style scoped>
.app-admin-page {
  width: 100%;
  height: 100%;
  background: #f5f7fb;
  overflow: auto;
}
.app-admin-card {
  width: 98%;
  min-height: 92%;
  margin: 1.5% auto;
  padding: 22px;
  background: #fff;
  box-shadow: #e0e0e0 5px 5px 10px;
  box-sizing: border-box;
}
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}
.toolbar-actions {
  display: flex;
  gap: 10px;
}
.title {
  color: #1f2d3d;
  font-size: 22px;
  font-weight: 700;
}
.muted {
  color: #7f8fa4;
  font-size: 13px;
  margin-top: 6px;
}
.section {
  border: 1px solid #e6ebf2;
  border-radius: 8px;
  padding: 20px;
  min-height: 430px;
  box-sizing: border-box;
}
.section-title {
  font-size: 17px;
  font-weight: 700;
  color: #1f2d3d;
  margin-bottom: 16px;
}
.status-line {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}
.status-text {
  color: #475569;
}
.user-panel {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: #f8fbff;
  border: 1px solid #e4edf8;
  border-radius: 8px;
  margin-bottom: 18px;
}
.avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
  background: #eef2f7;
}
.user-name {
  color: #1f2d3d;
  font-size: 18px;
  font-weight: 700;
}
.meta {
  margin-top: 14px;
}
.alert {
  margin-top: 14px;
}
.button-row {
  margin-top: 18px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.compact {
  justify-content: center;
}
.qr-section {
  text-align: center;
}
.qr-box {
  width: 260px;
  height: 260px;
  margin: 0 auto 14px;
  border: 1px solid #e6ebf2;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
}
.qr-box.empty {
  background: #f8fafc;
}
.qr-image {
  width: 230px;
  height: 230px;
}
.qr-placeholder {
  font-size: 64px;
  color: #b8c2cc;
}
.login-message {
  color: #1f2d3d;
  font-weight: 600;
  min-height: 22px;
}
.login-error {
  color: #f56c6c;
  margin-top: 8px;
  word-break: break-all;
}
</style>
